import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DataProvider } from './contexts/DataContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Members from './pages/Members';
import MemberDetail from './pages/MemberDetail';
import AddMember from './pages/AddMember';
import EditMember from './pages/EditMember';
import AddNote from './pages/AddNote';
import EditNote from './pages/EditNote';
import ManageCoaches from './pages/ManageCoaches';
import Settings from './pages/Settings';
import ProtectedRoute from './components/ProtectedRoute';
import InstallPrompt from './components/InstallPrompt';
import OfflineNotice from './components/OfflineNotice';
import UpdateNotice from './components/UpdateNotice';
import AppErrorBoundary from './components/AppErrorBoundary';
import GlobalRuntimeErrorOverlay from './components/GlobalRuntimeErrorOverlay';
import './App.css';

// Deployment Notes (for Cloudflare Pages / Vite):
// - Ensure the build command is `npm run build`.
// - Ensure the output directory is `dist`.
// - Cloudflare should serve the built `dist/index.html`, NOT the raw `index.html` that references `/src/main.jsx`.
// - If the app still goes white in production, use the GlobalRuntimeErrorOverlay + DevTools Console
//   to read the real error message and stack trace.

// Protected Settings Route that checks for Peter access
const ProtectedSettingsRoute = ({ children }) => {
  const { user } = useAuth();
  // Check if current user is Peter (case-insensitive)
  const isPeter =
    user?.name?.toLowerCase().includes('peter') ||
    user?.username?.toLowerCase().includes('peter');

  if (!isPeter) {
    return <Navigate to="/" replace />;
  }
  return children;
};

function AppContent() {
  return (
    <Router>
      {/* Global runtime-error overlay so we never get a silent white screen */}
      <GlobalRuntimeErrorOverlay />
      <OfflineNotice />
      <InstallPrompt />
      <UpdateNotice />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="members" element={<Members />} />
          <Route path="members/:memberId" element={<MemberDetail />} />
          <Route path="add-member" element={<AddMember />} />
          <Route path="edit-member/:memberId" element={<EditMember />} />
          <Route path="add-note/:memberId" element={<AddNote />} />
          <Route path="edit-note/:noteId/:memberId" element={<EditNote />} />
          <Route path="manage-coaches" element={<ManageCoaches />} />
          <Route
            path="settings"
            element={
              <ProtectedSettingsRoute>
                <Settings />
              </ProtectedSettingsRoute>
            }
          />
        </Route>
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <DataProvider>
        <AppErrorBoundary>
          <AppContent />
        </AppErrorBoundary>
      </DataProvider>
    </AuthProvider>
  );
}

export default App;